/**
 * LicenseStep
 *
 * Story 11: Registration Wizard - License Information
 *
 * Step 4 of 5 in registration wizard
 *
 * Features:
 * - License number input (optional)
 * - Insurance information textarea (optional)
 * - Certifications textarea (optional)
 * - File upload for documents (future enhancement)
 * - Form validation
 */

import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

export interface LicenseData {
  licenseNumber?: string;
  insuranceInfo?: string;
  certifications?: string;
}

interface LicenseStepProps {
  onNext: (data: LicenseData) => void;
  onBack: () => void;
  initialData?: Partial<LicenseData>;
}

const licenseSchema = z.object({
  licenseNumber: z.string().optional(),
  insuranceInfo: z.string().optional(),
  certifications: z.string().optional(),
});

type LicenseFormData = z.infer<typeof licenseSchema>;

export function LicenseStep({ onNext, onBack, initialData }: LicenseStepProps) {
  const {
    register,
    handleSubmit,
  } = useForm<LicenseFormData>({
    resolver: zodResolver(licenseSchema),
    defaultValues: initialData,
  });

  const onSubmit = (data: LicenseFormData) => {
    onNext(data);
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-neutral-900 mb-2">License & Certification</h2>
        <p className="text-neutral-600">
          Provide any relevant license and insurance information. All fields are optional but recommended.
        </p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <div>
          <label htmlFor="licenseNumber" className="block text-sm font-medium text-neutral-700 mb-1">
            License Number
          </label>
          <input
            id="licenseNumber"
            type="text"
            {...register('licenseNumber')}
            className="input w-full px-4 py-3 md:py-2 text-base border rounded-lg"
            placeholder="e.g., CA-123456"
          />
          <p className="mt-1 text-sm text-neutral-500">
            State-issued waste hauler or contractor license number
          </p>
        </div>

        <div>
          <label htmlFor="insuranceInfo" className="block text-sm font-medium text-neutral-700 mb-1">
            Insurance Information
          </label>
          <textarea
            id="insuranceInfo"
            {...register('insuranceInfo')}
            rows={4}
            className="input w-full px-4 py-3 md:py-2 text-base border rounded-lg min-h-24"
            placeholder="Provide details about your liability insurance, coverage amounts, policy numbers, etc."
          />
          <p className="mt-1 text-sm text-neutral-500">
            Include carrier name, policy number, and coverage amounts
          </p>
        </div>

        <div>
          <label htmlFor="certifications" className="block text-sm font-medium text-neutral-700 mb-1">
            Certifications
          </label>
          <textarea
            id="certifications"
            {...register('certifications')}
            rows={4}
            className="input w-full px-4 py-3 md:py-2 text-base border rounded-lg min-h-24"
            placeholder="List any relevant certifications (e.g., OSHA, DOT, hazmat handling, etc.)"
          />
          <p className="mt-1 text-sm text-neutral-500">
            Include certification names, issuing organizations, and expiration dates
          </p>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
          <p className="text-sm text-blue-800">
            <strong>Note:</strong> You can upload supporting documents after registration is approved.
          </p>
        </div>

        <div className="flex flex-col sm:flex-row sm:justify-between gap-3 pt-4">
          <button
            type="button"
            onClick={onBack}
            className="btn-secondary sm:w-auto order-2 sm:order-1"
          >
            Back
          </button>
          <button
            type="submit"
            className="btn-primary sm:w-auto order-1 sm:order-2"
          >
            Next
          </button>
        </div>
      </form>
    </div>
  );
}
